import { Head } from 'vite-react-ssg'
import {
  SITE_NAME,
  SITE_LOCALE,
  OG_IMAGE,
  OG_IMAGE_WIDTH,
  OG_IMAGE_HEIGHT,
  OG_IMAGE_ALT,
  absoluteUrl,
} from './siteConfig'

interface SeoProps {
  /** Full <title> text. */
  title: string
  description: string
  /** Route path used for canonical + og:url, e.g. '/sign-in'. Defaults to home. */
  path?: string
  /** Share image (root-relative or absolute). */
  image?: string
  /** Accessible alt text for the share image. */
  imageAlt?: string
  type?: 'website' | 'article' | 'product'
  /** When true, emit noindex,nofollow (for app/auth pages). */
  noindex?: boolean
  /** One or more JSON-LD structured-data objects. */
  jsonLd?: object | object[]
}

/**
 * Per-route <head> management. Rendered into the static HTML at build time by
 * vite-react-ssg, so crawlers and link scrapers see real meta tags without JS.
 */
export function Seo({
  title,
  description,
  path = '/',
  image = OG_IMAGE,
  imageAlt = OG_IMAGE_ALT,
  type = 'website',
  noindex = false,
  jsonLd,
}: SeoProps) {
  const canonical = absoluteUrl(path)
  const imageUrl = absoluteUrl(image)
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []

  return (
    <Head>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      <meta
        name="robots"
        content={noindex ? 'noindex,nofollow' : 'index,follow,max-image-preview:large'}
      />

      {/* Self-referential hreflang — clears "missing alternate / x-default / self-referring"
          even on a single-language site. Both point at this page's canonical URL. */}
      {!noindex && <link rel="alternate" hrefLang="en" href={canonical} />}
      {!noindex && <link rel="alternate" hrefLang="x-default" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={SITE_LOCALE} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:width" content={String(OG_IMAGE_WIDTH)} />
      <meta property="og:image:height" content={String(OG_IMAGE_HEIGHT)} />
      <meta property="og:image:alt" content={imageAlt} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {/* JSON-LD structured data */}
      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Head>
  )
}
